import React, { useState, useRef, useEffect } from 'react';
import { domId } from '../../utils/domIds';
import { ChevronDown } from 'lucide-react';
import { COUNTRIES, DEFAULT_COUNTRY, AVAILABLE_COUNTRIES } from '../../utils/countryUtils';
import Bandera from './Bandera';

/**
 * Indicador de región del header.
 *
 * Con más de una región en servicio es un selector desplegable. Con una sola
 * —la situación actual, tras retirar España— se muestra como etiqueta fija: un
 * desplegable de una única opción promete una elección que no existe.
 */
const CountrySelector = ({ country, onCountryChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const active = COUNTRIES[country] || COUNTRIES[DEFAULT_COUNTRY];
  const hayDondeElegir = AVAILABLE_COUNTRIES.length > 1;

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Región única: etiqueta informativa, sin interacción.
  if (!hayDondeElegir) {
    return (
      <div
        id="country-selector"
        title={`Catálogo de ${active.name}`}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 20,
          padding: '0 12px', height: 36, color: '#cccccc',
          fontSize: '0.8125rem', fontWeight: 600, whiteSpace: 'nowrap',
          userSelect: 'none',
        }}
      >
        <Bandera code={active.code} size={18} />
        <span>{active.code}</span>
      </div>
    );
  }

  return (
    <div id="country-selector" ref={ref} style={{ position: 'relative', userSelect: 'none' }}>
      {/* Trigger */}
      <button
        id="country-selector-boton"
        onClick={() => setOpen(v => !v)}
        className="selector-pais-boton"
        aria-label={`País actual: ${active.name}. Cambiar`}
      >
        <Bandera code={active.code} size={18} />
        <span>{active.code}</span>
        <ChevronDown
          size={12}
          style={{
            color: '#666',
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s',
          }}
        />
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 8px)',
          right: 0,
          background: '#111111',
          border: '1px solid #2a2a2a',
          borderRadius: 10,
          minWidth: 180,
          boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
          overflow: 'hidden',
          zIndex: 200,
        }}>
          {/* Header */}
          <div style={{ padding: '10px 14px 6px', borderBottom: '1px solid #1e1e1e' }}>
            <p style={{ fontSize: '0.7rem', color: '#666', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', margin: 0 }}>
              Seleccionar región
            </p>
          </div>

          {/* Options */}
          <div style={{ padding: 6 }}>
            {AVAILABLE_COUNTRIES.map(c => {
              const isActive = c.code === country;
              return (
                <button
                  id={domId('country-selector-opcion', c.code)}
                  key={c.code}
                  onClick={() => { onCountryChange(c.code); setOpen(false); }}
                  className={`selector-pais-opcion${isActive ? ' selector-pais-opcion--activa' : ''}`}
                >
                  <Bandera code={c.code} size={20} />
                  <div>
                    <p style={{ margin: 0, fontWeight: isActive ? 700 : 500 }}>{c.name}</p>
                    <p style={{ margin: 0, fontSize: '0.7rem', color: '#555' }}>
                      {c.currency} · {c.symbol}
                    </p>
                  </div>
                  {isActive && (
                    <div style={{
                      marginLeft: 'auto',
                      width: 7, height: 7, borderRadius: '50%',
                      background: '#FF4500', flexShrink: 0
                    }} />
                  )}
                </button>
              );
            })}
          </div>

          {/* Note */}
          <div style={{ padding: '8px 14px', borderTop: '1px solid #1e1e1e' }}>
            <p style={{ fontSize: '0.68rem', color: '#444', margin: 0 }}>
              La selección se guarda en tu navegador
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CountrySelector;
